import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar, Footer } from "@/components/yc/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { KeyRound, Sparkles } from "lucide-react";

const DemoAccess = () => {
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [code, setCode] = useState(search.get("code") || "");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const login = async (c) => {
        const value = (c || code).trim();
        if (!value) return;
        setBusy(true);
        setError("");
        try {
            const r = await api.post("/demo/login", { code: value });
            await refresh();
            navigate(`/${r.data.role || "brand"}/dashboard`, { replace: true });
        } catch (e) {
            setError(e.response?.data?.detail || "Could not start the demo — check your code");
        } finally {
            setBusy(false);
        }
    };

    // Magic-link support: /demo?code=DEMO-XXXXXX auto-logs in
    useEffect(() => {
        const c = search.get("code");
        if (c) login(c);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-yc-cream flex flex-col">
            <Navbar />
            <section className="flex-1 flex items-center justify-center px-6 py-16">
                <div className="yc-card p-10 max-w-md w-full">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-yc-pink" />
                        <div className="yc-overline">Brand demo</div>
                    </div>
                    <h1 className="font-heading text-4xl font-black tracking-tighter mt-3">Try Curated Chaos.</h1>
                    <p className="text-sm text-yc-ink/70 mt-3">
                        Enter the access code you were given to explore the full brand experience — vetted roster, campaign briefing and instant matching. No account needed.
                    </p>
                    <div className="mt-8">
                        <label className="yc-overline">Access code</label>
                        <div className="relative mt-2">
                            <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-yc-ink/40" />
                            <input
                                data-testid="demo-code-input"
                                className="yc-input pl-9 uppercase tracking-widest font-bold"
                                placeholder="DEMO-XXXXXX"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => { if (e.key === "Enter") login(); }}
                            />
                        </div>
                        {error && <div data-testid="demo-error" className="text-sm font-semibold text-yc-pink mt-3">{error}</div>}
                        <button data-testid="demo-start-btn" disabled={busy || !code.trim()} onClick={() => login()} className="yc-btn-primary w-full mt-5 disabled:opacity-50">
                            {busy ? "Starting demo…" : "Start the demo"}
                        </button>
                    </div>
                    <p className="text-xs text-yc-ink/50 mt-6">
                        Demo environments use fictional creators and reset automatically. Want a code? Email{" "}
                        <a href="mailto:info@curated-chaos.co.uk" className="text-yc-blue font-semibold">info@curated-chaos.co.uk</a>.
                    </p>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default DemoAccess;
